<template>
  <div
    class="flex items-center justify-between border-b h-14 px-4 relative custom:sticky top-0 z-[999]"
    :class="[mode === 'dark' ? 'bg-dark-90 border-dark-70' : 'bg-white-100']"
  >
    <div class="flex items-center gap-4">
      <div
        @click="collapseSidebar"
        class="w-[22px] h-[22px] cursor-pointer hidden custom:block"
      >
        <MenuIcon :size="22" :stroke-width="1.75" />
      </div>
      <slot></slot>
    </div>
    <div class="flex items-center gap-x-6 custom:gap-x-4">
      <div
        @click="$emit('toggle')"
        class="cursor-pointer"
        :class="[mode === 'dark' ? 'text-dark-15' : 'text-gray-600']"
      >
        <Sun v-if="mode === 'dark'" :size="22" :stroke-width="1.75" />
        <Moon v-else :size="22" :stroke-width="1.75" />
      </div>
      <AdvancedSearch :mode="mode" />
      <a
        v-if="tooltipText"
        href="https://seocopilotai.com/help"
        class="flex items-center hover:text-primary-100 gap-1.5 leading-none"
        :class="[mode === 'dark' ? 'text-dark-15' : 'text-gray-600']"
        target="_blank"
        v-tooltip="{
          content: `<div style='max-width: 550px;'>${tooltipText}</div>`,
          html: true,
          placement: 'auto',
          distance: 10,
        }"
      >
        <CircleHelp :size="22" :stroke-width="1.75" />
      </a>
      <Notifications :mode="mode" />
      <div
        class="relative"
        v-if="isLoggedIn && $route.name !== 'signup'"
        v-click-outside="hideDropdown"
      >
        <button
          class="flex items-center gap-1.5 leading-none relative"
          :class="[mode === 'dark' ? 'text-dark-15' : 'text-gray-600']"
          @click="toggleDropDown"
        >
          <img
            :src="userStore.gravatar()"
            alt="avatar"
            class="w-9 h-9 rounded-full object-cover"
          />
          <span
            class="absolute w-3 h-3 bottom-0 right-0 border-2 bg-success-100 rounded-full"
            :class="[mode === 'dark' ? 'border-dark-80' : 'border-white-100']"
          ></span>
        </button>
        <div
          class="absolute top-full mt-1 right-0 rounded-md min-w-60 border"
          :class="[
            dropdownClass,
            mode === 'dark' ? 'bg-dark-90 border-dark-70' : 'bg-white-100',
          ]"
        >
          <div
            v-if="user.first_name || user.last_name"
            class="border-b px-2 py-2.5"
            :class="[
              mode === 'dark'
                ? 'bg-dark-70 border-dark-70'
                : 'bg-primary-100 bg-opacity-5',
            ]"
          >
            <span class="font-medium leading-none block"
              >{{ user.first_name || "" }} {{ user.last_name || "" }}</span
            >
            <ul class="grid gap-1 mt-2 empty:hidden" v-if="user.current_tier">
              <li
                class="text-[15px]"
                :class="[mode === 'dark' ? 'text-dark-15' : 'text-gray-600']"
              >
                {{ accountType(user?.current_tier?.tier_name) }} Account
                {{
                  user?.current_tier?.price
                    ? "($" + user?.current_tier?.price + "/month)"
                    : ""
                }}
              </li>
            </ul>
          </div>
          <div class="py-2">
            <RouterLink :to="{ name: 'account' }" v-slot="{ navigate }">
              <a
                class="flex items-center gap-2 p-2 bg-opacity-5"
                :class="[
                  mode === 'dark'
                    ? 'text-dark-15 hover:text-white-100 hover:bg-dark-70'
                    : 'hover:bg-primary-100 text-gray-600 hover:text-black-100 hover:bg-opacity-5',
                ]"
                @click="navigate"
              >
                <CircleUser :size="21" :stroke-width="1.75" />
                Account
              </a>
            </RouterLink>
            <RouterLink
              v-if="isTeamTier"
              :to="{ name: 'team-members' }"
              v-slot="{ navigate }"
            >
              <a
                class="flex items-center gap-2 p-2 bg-opacity-5"
                @click="navigate"
                :class="[
                  mode === 'dark'
                    ? 'text-dark-15 hover:text-white-100 hover:bg-dark-70'
                    : 'hover:bg-primary-100 text-gray-600 hover:text-black-100 hover:bg-opacity-5',
                ]"
              >
                <Users :size="21" :stroke-width="1.75" />
                Team
              </a>
            </RouterLink>
            <RouterLink
              :to="{
                name: 'account-subscription',
                query: { coupon: 's3oArph3' },
              }"
              v-slot="{ navigate }"
              v-if="user.can_upgrade"
            >
              <a
                class="flex items-center gap-2 p-2 bg-opacity-5"
                @click="navigate"
                :class="[
                  mode === 'dark'
                    ? 'text-dark-15 hover:text-white-100 hover:bg-dark-70'
                    : 'hover:bg-primary-100 text-gray-600 hover:text-black-100 hover:bg-opacity-5',
                ]"
              >
                <CreditCard :size="21" :stroke-width="1.75" />
                Subscription
              </a>
            </RouterLink>
            <RouterLink :to="{ name: 'integrations' }" v-slot="{ navigate }">
              <a
                class="flex items-center gap-2 p-2 bg-opacity-5"
                @click="navigate"
                :class="[
                  mode === 'dark'
                    ? 'text-dark-15 hover:text-white-100 hover:bg-dark-70'
                    : 'hover:bg-primary-100 text-gray-600 hover:text-black-100 hover:bg-opacity-5',
                ]"
              >
                <Blocks :size="21" :stroke-width="1.75" />
                Integrations
              </a>
            </RouterLink>
            <a
              class="flex items-center gap-2 p-2 bg-opacity-5"
              :class="[
                mode === 'dark'
                  ? 'text-dark-15 hover:text-white-100 hover:bg-dark-70'
                  : 'hover:bg-primary-100 text-gray-600 hover:text-black-100 hover:bg-opacity-5',
              ]"
              href="#"
              @click.prevent="signOut"
            >
              <LogOut
                :size="21"
                :stroke-width="1.75"
                class="transform rotate-180"
              />
              Log out
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useUserStore } from "./../stores/user";
import Notifications from "./Notifications.vue";
import {
  Blocks,
  CircleHelp,
  CircleUser,
  CreditCard,
  LogOut,
  MenuIcon,
  Users,
  Sun,
  Moon,
} from "lucide-vue-next";
import AdvancedSearch from "./AdvancedSearch.vue";

export default {
  setup() {
    const userStore = useUserStore();
    return {
      isLoggedIn: computed(() => userStore.isLoggedIn),
      user: computed(() => userStore.user),
      userStore: userStore,
    };
  },
  components: {
    AdvancedSearch,
    CircleHelp,
    CircleUser,
    Users,
    CreditCard,
    Blocks,
    LogOut,
    MenuIcon,
    Sun,
    Moon,
    Notifications,
  },
  props: {
    mode: String,
  },
  data() {
    return {
      dropdownClass: "hidden",
      isCollapsed: false,
    };
  },
  mounted() {
    window.addEventListener("isCollapsed", (event) => {
      this.isCollapsed = event.detail.isCollapsed;
    });
  },
  methods: {
    toggleDropDown() {
      this.dropdownClass = this.dropdownClass === "block" ? "hidden" : "block";
    },
    hideDropdown() {
      if (this.dropdownClass === "block") {
        this.dropdownClass = "hidden";
      }
    },
    signOut() {
      this.userStore.signOut();
      this.$router.push({ name: "signin" });
      this.$router.go(0);
    },
    accountType(tier) {
      tier = tier.toLowerCase();
      switch (tier) {
        case "trial":
          return "Trial";
        case "basic monthly":
          return "Basic (monthly)";
        case "team monthly":
          return "Team (monthly)";
        case "basic annual":
          return "Basic (annual)";
        case "team annual":
          return "Team (annual)";
        case "spy basic monthly":
          return "Keyword Spy Basic (monthly)";
        case "spy team monthly":
          return "Keyword Spy Team (monthly)";
        default:
          return tier.toUpperCase();
      }
    },
    collapseSidebar() {
      this.isCollapsed = !this.isCollapsed;
      window.dispatchEvent(
        new CustomEvent("collapseSidebar", {
          detail: {
            isCollapsed: this.isCollapsed,
          },
        })
      );
    },
  },
  computed: {
    isTeamTier() {
      return this.user.team_tier || false;
    },
    tooltipText() {
      if (this.$route.name === "serp-keyword-spy") {
        return "This is where you run keyword reports to spy on competitors and automatically build your Google AI models for the absolute best keywords (you can also click through on the help button to get quick written tool tips and watch video tutorials).";
      } else if (this.$route.name === "zebra-ai") {
        return "Use AI Spy to Employ Next Level AI to Analyze and Generate Various Forms of Important SEO Content. See new features in early development​ <br/>Use AI to Analyze and Generate Helpful / Review Content, Workshop URL/Title/Meta/H1, AI Detection, AI Content Improver and Featured Snippet Generator! (you can also click through on the help button to get quick written tool tips and watch video tutorials).";
      } else if (this.$route.name === "dashboard") {
        return "This is the dashboard. See your recent keyword reports, check your usage status, watch our tutorials on how to use the system (you can also click through on the help button to get quick written tool tips), and stay up to date with keyword spy news!";
      } else if (this.$route.name === "reports") {
        return "Check the results of all your keyword metrics (you can also click through on the help button to get quick written tool tips and watch video tutorials).";
      } else if (this.$route.name === "serp-report-ta") {
        return "These are the Google AI's that keyword spy analyzes including a Competitor Report <br/>- Useful for planning SEO Content, Checking competitors, and keyword results <br/>- To employ these results go to Article Builder <br /> Check the results of all your keyword metrics (you can also click through on the help button to get quick written tool tips and watch video tutorials).";
      } else if (this.$route.name === "projects") {
        return "Handy way to organize reports into projects (you can also click through on the help button to get quick written tool tips and watch video tutorials).";
      } else if (this.$route.name === "editor") {
        return "After Your Report is done, use Advanced AI to Generate / Optimize ALL of your SEO content: URLs, Titles, METAs, Headings, Images and Content with the Perfect Metrics! (You can also click through on the help button to get quick written tool tips and watch video tutorials).";
      } else if (this.$route.name === "KeywordPlannerDetail") {
        return "Enter in your niche/topic, our next generation AI will calculate the most related keywords with the best $$$ that are ALSO easiest to rank for. Thinks like a top-level SEO Consultant!";
      } else if (this.$route.name === "notifications") {
        return "Notification list";
      } else if (this.$route.name === "audit") {
        return "Audit page";
      } else if (this.$route.name === "audit-details") {
        return "Audit page details";
      } else if (this.$route.name === "KeywordPlanner") {
        return "START HERE Start with a base or seed keyword, and use Advanced AI to discover the most lucrative and easiest keywords and niches to go for! Plan Money Pages and Topical clusters.";
      }
      return null;
    },
  },
};
</script>
